import React from 'react';
import { getEasing, getDuration, getContext, isAnimationDisabled } from 'common';
import { useSpring, animated } from 'react-spring';
import _merge from 'lodash/merge';
import ChevronUpIcon from '@nokia-csf-uxr/ccfk-assets/ChevronUpIcon';
import IconButton, { IconButtonProps } from 'components/IconButton';
import TOKENS from '@nokia-csf-uxr/freeform-design-tokens';
import { getElevationStyle, ElevationProps } from 'components/Elevation';

import Styled from './styles';

export interface UtilityDrawerHideButtonProps extends React.ComponentPropsWithRef<'div'> {
  /** Control animation config here. This uses react-spring transitions config. See https://react-spring.io for more information. */
  animationProps?: any;
  /* IconButton props, useful for forwarding props to <IconButton /> */
  iconButtonProps?: Partial<IconButtonProps>;
  /** Override the Elevation properties. See Elevation component for details. */
  elevationProps?: Partial<ElevationProps>;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** If false, the Utility Drawer should hide. */
  show?: boolean;
}

/** Utility Drawer show/hide IconButton */
const UtilityDrawerHideButton = React.forwardRef<HTMLElement, UtilityDrawerHideButtonProps>((props: UtilityDrawerHideButtonProps, ref) => getContext(({ RTL, disableAnimation }) => {
  const {
    show, elevationProps, animationProps, iconButtonProps, disableAnimation: propsdisableAnimation, ...otherProps
  } = props;
  const isRTL = RTL;
  const { style, ...otherIconButtonProps } = iconButtonProps || {};

  const buttonAnimation = useSpring(animationProps || {
    transform: show ? 'rotate(180deg)' : 'rotate(0deg)',
    config: {
      duration: getDuration(TOKENS.SPEED.SPEED_SLOW),
      easing: getEasing(TOKENS.TRANSITION.TRANSITION_DECELERATE)
    },
    immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation),
  });

  const buttonBgAnimation = useSpring({
    height: show ? '0rem' : '2rem',
    config: {
      duration: getDuration(TOKENS.SPEED.SPEED_SLOW),
      easing: getEasing(TOKENS.TRANSITION.TRANSITION_DECELERATE)
    },
    immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation),
  });

  const resolvedIconButtonStyle = _merge(
    {},
    {
      zIndex: 1
    },
    style
  );
  return (
    <Styled.HideButtonWrapper ref={ref} $rtl={isRTL} {...otherProps}>
      <Styled.HideButtonOverlayWrapper as={animated.div} style={buttonBgAnimation}>
        <Styled.HideButtonOverlay
          style={{
            backgroundColor: getElevationStyle(elevationProps).backgroundColor
          }}
        />
      </Styled.HideButtonOverlayWrapper>

      <IconButton style={resolvedIconButtonStyle} {...otherIconButtonProps}>
        <animated.div style={buttonAnimation}>
          <ChevronUpIcon />
        </animated.div>
      </IconButton>
    </Styled.HideButtonWrapper>
  );
}));

UtilityDrawerHideButton.defaultProps = {
  animationProps: undefined,
  disableAnimation: undefined,
  iconButtonProps: undefined,
  elevationProps: {
    elevationIndex: 2,
    dark: true
  },
  show: true
};

UtilityDrawerHideButton.displayName = 'UtilityDrawerHideButton';

export default UtilityDrawerHideButton;
